/**
 * AI Tool Executor
 *
 * Handles parsing tool calls from LLM responses and executing them.
 * Implements the ReAct pattern for structured tool calling.
 */

import type { ToolCall, ToolResult, PendingConfirmation, ToolExecutionOptions, DEFAULT_EXECUTION_OPTIONS } from './types';
import { toolRegistry } from './registry';
import { emitFromToolResult } from '../engine/userActionEmitter';
import { projectToolExecutors } from './projectTools';
import { calendarToolExecutors } from './calendarTools';
import { contactToolExecutors } from './contactTools';
import { accountingToolExecutors } from './accountingTools';
import { hubToolExecutors } from './hubTools';
import { supportToolExecutors } from './supportTools';

// =============================================================================
// TOOL EXECUTORS REGISTRY
// =============================================================================

/**
 * Combined registry of all tool executors
 */
const toolExecutors: Record<string, (params: Record<string, unknown>) => Promise<unknown>> = {
  ...projectToolExecutors,
  ...calendarToolExecutors,
  ...contactToolExecutors,
  ...accountingToolExecutors,
  ...hubToolExecutors,
  ...supportToolExecutors,
};

// =============================================================================
// TOOL CALL PARSING
// =============================================================================

/**
 * Regular expressions to extract JSON from LLM responses
 */
const JSON_PATTERNS = [
  // Match JSON object with tool field
  /\{\s*"(?:thought|tool)"[\s\S]*?"tool"\s*:\s*"[^"]+"\s*,[\s\S]*?"parameters"\s*:\s*\{[\s\S]*?\}\s*\}/,
  // Match JSON in code blocks
  /```(?:json)?\s*(\{[\s\S]*?"tool"[\s\S]*?\})\s*```/,
  // Match simple JSON object
  /(\{[\s\S]*?"tool"\s*:\s*"[^"]+"\s*[\s\S]*?\})/,
];

/**
 * Parse a tool call from an LLM response
 *
 * @param response - The raw LLM response text
 * @returns The parsed ToolCall or null if no valid tool call found
 */
export function parseToolCall(response: string): ToolCall | null {
  if (!response || typeof response !== 'string') {
    return null;
  }

  // Try each pattern
  for (const pattern of JSON_PATTERNS) {
    const match = response.match(pattern);
    if (match) {
      try {
        // Get the JSON string (might be in capture group)
        const jsonStr = match[1] || match[0];
        const parsed = JSON.parse(jsonStr);

        // Validate required fields
        if (typeof parsed.tool === 'string' && parsed.parameters !== undefined) {
          return {
            thought: parsed.thought || undefined,
            tool: parsed.tool,
            parameters: parsed.parameters || {},
          };
        }
      } catch {
        // Continue to next pattern
        continue;
      }
    }
  }

  // Try to find any JSON object in the response
  try {
    const jsonStart = response.indexOf('{');
    const jsonEnd = response.lastIndexOf('}');

    if (jsonStart !== -1 && jsonEnd > jsonStart) {
      const jsonStr = response.substring(jsonStart, jsonEnd + 1);
      const parsed = JSON.parse(jsonStr);

      if (typeof parsed.tool === 'string') {
        return {
          thought: parsed.thought || undefined,
          tool: parsed.tool,
          parameters: parsed.parameters || {},
        };
      }
    }
  } catch {
    // No valid JSON found
  }

  return null;
}

/**
 * Check if a response contains a tool call
 */
export function hasToolCall(response: string): boolean {
  return parseToolCall(response) !== null;
}

/**
 * Extract any natural language text before a tool call
 */
export function extractTextBeforeToolCall(response: string): string {
  const toolCall = parseToolCall(response);
  if (!toolCall) {
    return response;
  }

  // Find where the JSON starts
  const jsonStart = response.indexOf('{');
  if (jsonStart > 0) {
    const textBefore = response.substring(0, jsonStart).trim();
    return textBefore;
  }

  return '';
}

// =============================================================================
// TOOL EXECUTION
// =============================================================================

/**
 * Check if a tool requires confirmation before execution
 */
export function requiresConfirmation(toolName: string): boolean {
  return toolRegistry.requiresConfirmation(toolName);
}

/**
 * Get the tool definition
 */
export function getToolDefinition(toolName: string) {
  return toolRegistry.getTool(toolName);
}

/**
 * Create a pending confirmation object for a delete operation
 */
export function createPendingConfirmation(call: ToolCall): PendingConfirmation {
  const tool = toolRegistry.getTool(call.tool);
  const params = call.parameters;

  // Build a human-readable description
  let description = '';
  let entityType = '';
  let entityName = '';

  switch (call.tool) {
    case 'delete_project':
      entityType = 'progetto';
      entityName = String(params.projectName || params.id || 'sconosciuto');
      description = `Eliminare il progetto "${entityName}"? Questa azione eliminerà anche tutti i task associati ed è irreversibile.`;
      break;
    case 'delete_task':
      entityType = 'task';
      entityName = String(params.taskName || params.id || 'sconosciuto');
      description = `Eliminare il task "${entityName}"? Questa azione è irreversibile.`;
      break;
    case 'delete_event':
      entityType = 'evento';
      entityName = String(params.eventTitle || params.id || 'sconosciuto');
      description = `Eliminare l'evento "${entityName}" dal calendario? Questa azione è irreversibile.`;
      break;
    case 'delete_contact':
      entityType = 'contatto';
      entityName = String(params.contactName || params.id || 'sconosciuto');
      description = `Eliminare il contatto "${entityName}"? Questa azione rimuoverà anche le conversazioni associate ed è irreversibile.`;
      break;
    case 'delete_milestone':
      entityType = 'milestone';
      entityName = String(params.id || 'sconosciuta');
      description = `Eliminare la milestone? Questa azione è irreversibile.`;
      break;
    case 'delete_message':
      entityType = 'messaggio';
      entityName = String(params.id || 'sconosciuto');
      description = `Eliminare il messaggio? Questa azione è irreversibile.`;
      break;
    case 'delete_email':
      entityType = 'email';
      entityName = String(params.id || 'sconosciuta');
      description = `Eliminare l'email? Questa azione è irreversibile.`;
      break;
    case 'delete_support_request':
      entityType = 'richiesta supporto';
      entityName = String(params.id || 'sconosciuta');
      description = `Eliminare la richiesta di supporto? Questa azione è irreversibile.`;
      break;
    case 'delete_client_log':
      entityType = 'log';
      entityName = String(params.id || 'sconosciuto');
      description = `Eliminare il log del client? Questa azione è irreversibile.`;
      break;
    default:
      entityType = tool?.category || 'elemento';
      description = `Confermi l'operazione "${call.tool}"? Questa azione potrebbe essere irreversibile.`;
  }

  return {
    tool: call.tool,
    parameters: call.parameters,
    description,
    entityType,
    entityId: String(params.id || ''),
    entityName,
  };
}

/**
 * Execute a tool call and return the result
 */
export async function executeToolCall(
  call: ToolCall,
  options: Partial<ToolExecutionOptions> = {}
): Promise<ToolResult> {
  const { timeout = 30000 } = options;

  // Check if tool exists
  if (!toolRegistry.hasTool(call.tool)) {
    return {
      success: false,
      error: `Tool sconosciuto: "${call.tool}". Verifica il nome del tool.`,
      tool: call.tool,
    };
  }

  // Check if executor exists
  const executor = toolExecutors[call.tool];
  if (!executor) {
    return {
      success: false,
      error: `Executor non trovato per il tool: "${call.tool}"`,
      tool: call.tool,
    };
  }

  try {
    // Execute with timeout
    const result = await Promise.race([
      executor(call.parameters),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout: operazione troppo lunga')), timeout)
      ),
    ]);

    const toolResult: ToolResult = {
      success: true,
      data: result,
      tool: call.tool,
    };

    // Emit engine event for workflow matching and notifications
    emitFromToolResult(call.tool, call.parameters, { success: true, data: toolResult.data, tool: call.tool }, options.accountId || 'system');

    return toolResult;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Errore sconosciuto';
    return {
      success: false,
      error: errorMessage,
      tool: call.tool,
    };
  }
}

/**
 * Format a tool result as a string for injection into the conversation
 */
export function formatToolResult(result: ToolResult): string {
  if (result.success) {
    return `[TOOL RESULT - ${result.tool}]\n${JSON.stringify(result.data, null, 2)}`;
  } else {
    return `[TOOL ERROR - ${result.tool}]\n${result.error}`;
  }
}

/**
 * Create a message to inject the tool result back to the LLM
 */
export function createToolResultMessage(result: ToolResult): string {
  const header = result.success
    ? `Ho eseguito "${result.tool}" con successo.`
    : `Errore nell'esecuzione di "${result.tool}".`;

  const details = result.success
    ? `Risultato:\n\`\`\`json\n${JSON.stringify(result.data, null, 2)}\n\`\`\``
    : `Errore: ${result.error}`;

  return `${header}\n\n${details}\n\nOra rispondi all'utente in italiano, riassumendo cosa è stato fatto.`;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get a list of all available tools
 */
export function getAvailableTools(): string[] {
  return toolRegistry.getToolNames();
}

/**
 * Get tools by category
 */
export function getToolsByCategory(category: string) {
  return toolRegistry.getToolsByCategory(category as 'projects' | 'tasks' | 'calendar' | 'contacts' | 'accounting' | 'hub' | 'support');
}

/**
 * Validate tool parameters against schema
 */
export function validateToolParameters(toolName: string, params: Record<string, unknown>): { valid: boolean; errors: string[] } {
  const tool = toolRegistry.getTool(toolName);
  if (!tool) {
    return { valid: false, errors: [`Tool "${toolName}" non trovato`] };
  }

  const errors: string[] = [];
  const required = (tool.parameters.required as string[]) || [];

  // Check required parameters
  for (const param of required) {
    if (params[param] === undefined || params[param] === null || params[param] === '') {
      errors.push(`Parametro obbligatorio mancante: "${param}"`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get a summary of what a tool call will do
 */
export function describeToolCall(call: ToolCall): string {
  const tool = toolRegistry.getTool(call.tool);
  if (!tool) {
    return `Eseguire azione sconosciuta: ${call.tool}`;
  }

  const operation = {
    create: 'Creare',
    read: 'Cercare/Leggere',
    update: 'Modificare',
    delete: 'Eliminare',
  }[tool.operation];

  const category = {
    projects: 'progetto',
    tasks: 'task',
    calendar: 'evento calendario',
    contacts: 'contatto',
    accounting: 'elemento contabilità',
    hub: 'comunicazione',
    support: 'supporto',
  }[tool.category];

  return `${operation} ${category}: ${tool.description}`;
}
